/**
 * Web Push Notifications — Firebase Cloud Messaging for browsers.
 *
 * Mirrors ../services/pushNotifications.js (Capacitor/mobile) so both platforms
 * share the same registration API (`/devices`), the same "tap" event
 * (`push:action`) and the same foreground banner event (`push:received`).
 *
 * Requires REACT_APP_ENABLE_WEB_PUSH=true and REACT_APP_FIREBASE_* config
 * (see .env.example), plus a Web app registered on the same Firebase project
 * used for Android.
 */

import { getMessagingInstanceSafe } from "../config/firebase";
import {
  getStoredPushToken,
  setStoredPushToken,
  registerDevice,
  unregisterStoredDevice,
} from "../api/devices";
import { dispatchPushAction } from "./pushNotifications";

const SW_URL = "/firebase-messaging-sw.js";

let webInitialized = false;
let webInitInFlight = null;
let swRegistration = null;
let messageListenerAttached = false;
let lastSyncedWebKey = null;

function isWebPushEnabled() {
  const raw = process.env.REACT_APP_ENABLE_WEB_PUSH;
  if (raw === undefined || raw === "") {
    return String(process.env.REACT_APP_ENABLE_PUSH || "").toLowerCase() === "true";
  }
  return String(raw).toLowerCase() === "true";
}

function isWebPushSupported() {
  return (
    typeof window !== "undefined" &&
    "Notification" in window &&
    typeof navigator !== "undefined" &&
    "serviceWorker" in navigator
  );
}

function getPushErrorMessage(err) {
  return err?.response?.data?.message || err?.message || "unknown error";
}

/** Relays taps on notifications delivered while the app tab was already open. */
function attachServiceWorkerMessageListener() {
  if (messageListenerAttached || !("serviceWorker" in navigator)) return;
  messageListenerAttached = true;

  navigator.serviceWorker.addEventListener("message", (event) => {
    if (event.data?.type !== "ess-push-action") return;
    dispatchPushAction({ notification: { data: event.data.data || {} } });
  });
}

async function registerServiceWorker() {
  if (swRegistration) return swRegistration;
  swRegistration = await navigator.serviceWorker.register(SW_URL);
  return swRegistration;
}

function currentUserId() {
  try {
    const raw = localStorage.getItem("ess_user");
    if (!raw) return "";
    return JSON.parse(raw)?.id || "";
  } catch {
    return "";
  }
}

async function syncWebTokenToBackend(token) {
  if (!token) return;

  const syncKey = `${currentUserId()}:${token}`;
  if (syncKey === lastSyncedWebKey) return;

  await registerDevice({
    token,
    platform: "web",
    deviceName:
      typeof navigator !== "undefined" ? navigator.userAgent?.slice(0, 190) : undefined,
    appVersion: process.env.REACT_APP_VERSION || undefined,
  });
  lastSyncedWebKey = syncKey;
  setStoredPushToken(token);
  console.info("[WebPush] Token registered backend");
}

export async function initWebPushNotifications() {
  if (!isWebPushSupported() || !isWebPushEnabled()) return;
  if (webInitialized) {
    const cached = getStoredPushToken();
    if (cached) {
      await syncWebTokenToBackend(cached).catch((err) => {
        console.error("[WebPush] Backend re-sync failed:", getPushErrorMessage(err));
      });
    }
    return;
  }
  if (webInitInFlight) return webInitInFlight;

  webInitInFlight = (async () => {
    try {
      const messaging = await getMessagingInstanceSafe();
      if (!messaging) {
        console.info("[WebPush] Not supported/configured — skipped");
        return;
      }

      const registration = await registerServiceWorker();

      if (Notification.permission === "default") {
        const permission = await Notification.requestPermission();
        if (permission !== "granted") {
          console.warn("[WebPush] Permission not granted");
          return;
        }
      } else if (Notification.permission !== "granted") {
        console.warn("[WebPush] Permission previously denied");
        return;
      }

      const { getToken, onMessage } = await import("firebase/messaging");
      const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;

      const token = await getToken(messaging, {
        vapidKey,
        serviceWorkerRegistration: registration,
      });

      if (!token) {
        console.warn("[WebPush] No registration token available");
        return;
      }

      await syncWebTokenToBackend(token).catch((err) => {
        console.error("[WebPush] Backend registration failed:", getPushErrorMessage(err));
      });

      attachServiceWorkerMessageListener();

      // Foreground: tab is open and focused — show an in-app banner, same as mobile.
      onMessage(messaging, (payload) => {
        console.info("[WebPush] Foreground message:", payload);
        const data = payload?.data || {};
        window.dispatchEvent(
          new CustomEvent("push:received", {
            detail: {
              title: data.title || "إشعار جديد",
              body: data.body || data.message || "",
              data,
            },
          }),
        );
      });

      webInitialized = true;
    } catch (err) {
      console.error("[WebPush] Init failed:", getPushErrorMessage(err));
    }
  })();

  try {
    await webInitInFlight;
  } finally {
    webInitInFlight = null;
  }
}

/** Called on logout — best-effort backend + FCM subscription cleanup. */
export async function unregisterWebPush() {
  try {
    const messaging = await getMessagingInstanceSafe();
    if (messaging) {
      const { deleteToken } = await import("firebase/messaging");
      await deleteToken(messaging).catch(() => {});
    }
  } finally {
    await unregisterStoredDevice().catch(() => {});
    lastSyncedWebKey = null;
    webInitialized = false;
  }
}

export function cleanupWebPushNotifications() {
  webInitialized = false;
  lastSyncedWebKey = null;
}
